import React, { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import SEO from '../components/SEO'
import LightboxModal from '../components/LightboxModal'
import { fetchCollectionList } from '../lib/cms'

export default function GalleryPage() {
  const [galleries, setGalleries] = useState([])
  const [loading, setLoading] = useState(true)
  const [activeFilter, setActiveFilter] = useState('alle')
  const [lightboxState, setLightboxState] = useState({
    isOpen: false,
    images: [],
    index: 0,
    caption: '',
  })

  useEffect(() => {
    let isMounted = true
    async function loadData() {
      setLoading(true)
      try {
        const list = await fetchCollectionList('galleri')
        if (isMounted) {
          // Normalize items so 'billeder' is always an array of string URLs
          const normalized = (Array.isArray(list) ? list : []).map((item) => ({
            ...item,
            billeder: Array.isArray(item.billeder)
              ? item.billeder.filter(Boolean)
              : item.billeder
              ? [item.billeder]
              : [],
          }))
          setGalleries(normalized)
        }
      } catch (err) {
        console.warn('Fejl ved indlæsning af galleri fra CMS:', err)
      } finally {
        if (isMounted) setLoading(false)
      }
    }

    loadData()
    return () => {
      isMounted = false
    }
  }, [])

  // Filtered collections
  const filteredGalleries =
    activeFilter === 'alle'
      ? galleries
      : galleries.filter((g) => (g.slug?._ || g.slug || g.id) === activeFilter)

  // Total count of images across all loaded galleries
  const totalImageCount = galleries.reduce((acc, curr) => acc + (curr.billeder?.length || 0), 0)

  const openLightbox = (images, index, caption) => {
    setLightboxState({
      isOpen: true,
      images,
      index,
      caption,
    })
  }

  const closeLightbox = () => {
    setLightboxState((prev) => ({ ...prev, isOpen: false }))
  }

  return (
    <>
      <SEO
        title="Galleri – Krüger Wet Blaster"
        description="Se billeder af vores danskproducerede Krüger Wet Blaster vandpoleringsmaskiner i værkstedsmiljø samt før- og efterresultater."
      />

      {/* Header Banner */}
      <section className="bg-slate-900 text-white border-b border-slate-800 py-12 lg:py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded bg-sky-500/10 border border-sky-500/20 text-sky-400 text-xs font-semibold uppercase tracking-wider mb-4">
              <i className="fa-solid fa-camera" aria-hidden="true" />
              <span>Billedgalleri</span>
            </div>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight mb-4">
              Galleri
            </h1>
            <p className="text-slate-300 text-base sm:text-lg leading-relaxed">
              Oplev Krüger Wet Blaster maskiner i værkstedsmiljø og se de imponerende før- og efterresultater med skånsom, kemi-fri vandpolering.
            </p>
          </div>
        </div>
      </section>

      {/* Content Section */}
      <section className="py-12 sm:py-16 lg:py-20 bg-slate-50 min-h-[50vh]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Filter Pills if multiple galleries exist */}
          {galleries.length > 1 && (
            <div className="flex flex-wrap items-center gap-2 pb-8 border-b border-slate-200 mb-10">
              <span className="text-xs font-semibold uppercase tracking-wider text-slate-500 mr-2">
                Kategori:
              </span>
              <button
                type="button"
                onClick={() => setActiveFilter('alle')}
                className={`px-4 py-2 text-sm font-medium rounded-md transition-all cursor-pointer ${
                  activeFilter === 'alle'
                    ? 'bg-sky-600 text-white shadow-sm'
                    : 'bg-white text-slate-700 hover:bg-slate-100 border border-slate-300'
                }`}
              >
                Alle billeder ({totalImageCount})
              </button>
              {galleries.map((gal) => {
                const key = gal.slug?._ || gal.slug || gal.id
                const count = gal.billeder?.length || 0
                return (
                  <button
                    key={key}
                    type="button"
                    onClick={() => setActiveFilter(key)}
                    className={`px-4 py-2 text-sm font-medium rounded-md transition-all cursor-pointer ${
                      activeFilter === key
                        ? 'bg-sky-600 text-white shadow-sm'
                        : 'bg-white text-slate-700 hover:bg-slate-100 border border-slate-300'
                    }`}
                  >
                    {gal.titel} ({count})
                  </button>
                )
              })}
            </div>
          )}

          {/* Loading State */}
          {loading && (
            <div className="py-24 text-center">
              <i className="fa-solid fa-spinner fa-spin text-4xl text-sky-500 mb-4" />
              <p className="text-slate-600 font-medium">Henter billeder fra galleriet...</p>
            </div>
          )}

          {/* Empty State */}
          {!loading && (!galleries.length || totalImageCount === 0) && (
            <div className="py-16 text-center max-w-md mx-auto bg-white rounded-xl border border-slate-200 p-8 shadow-sm">
              <div className="w-14 h-14 mx-auto rounded-full bg-sky-50 text-sky-600 flex items-center justify-center text-2xl mb-4">
                <i className="fa-solid fa-images" aria-hidden="true" />
              </div>
              <h2 className="text-lg font-bold text-slate-900 mb-2">Ingen billeder fundet</h2>
              <p className="text-sm text-slate-600 mb-6">
                Der er endnu ikke tilføjet billeder til galleriet i appen. Billeder kan administreres direkte i CMS-dashboardet.
              </p>
              <Link
                to="/kontakt"
                className="inline-flex items-center gap-2 bg-sky-600 hover:bg-sky-500 text-white text-sm font-semibold px-4 py-2.5 rounded-md transition-colors"
              >
                <span>Kontakt os for referencebilleder</span>
                <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true" />
              </Link>
            </div>
          )}

          {/* Gallery Groups */}
          {!loading && filteredGalleries.length > 0 && (
            <div className="space-y-16">
              {filteredGalleries.map((item) => {
                const galleryKey = item.slug?._ || item.slug || item.id
                const images = item.billeder || []
                if (images.length === 0) return null

                return (
                  <div key={galleryKey} className="bg-white rounded-xl p-6 sm:p-8 border border-slate-200 shadow-sm">
                    {/* Header per gallery entry */}
                    <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-3 mb-6 pb-4 border-b border-slate-100">
                      <div>
                        <h2 className="text-xl sm:text-2xl font-bold text-slate-900 tracking-tight">
                          {item.titel}
                        </h2>
                        {item.billedtekster && (
                          <p className="text-sm sm:text-base text-slate-600 mt-1 max-w-2xl">
                            {item.billedtekster}
                          </p>
                        )}
                      </div>
                      <span className="text-xs font-semibold uppercase tracking-wider text-slate-400">
                        {images.length} {images.length === 1 ? 'billede' : 'billeder'}
                      </span>
                    </div>

                    {/* Image Grid */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-6">
                      {images.map((imgUrl, idx) => {
                        const optimized = imgUrl.includes('assets.auxodev.dk')
                          ? imgUrl.replace(
                              'assets.auxodev.dk/',
                              'assets.auxodev.dk/cdn-cgi/image/width=800,quality=80,format=auto/'
                            )
                          : imgUrl

                        return (
                          <button
                            key={idx}
                            type="button"
                            onClick={() => openLightbox(images, idx, item.titel)}
                            className="group relative aspect-4/3 overflow-hidden rounded-lg bg-slate-100 border border-slate-200 focus:outline-none focus:ring-2 focus:ring-sky-500 cursor-pointer shadow-sm hover:shadow-md transition-shadow"
                            aria-label={`Åbn ${item.titel} billede ${idx + 1} i stor visning`}
                          >
                            <img
                              src={optimized}
                              alt={`${item.titel} - billede ${idx + 1}`}
                              loading="lazy"
                              className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                            />
                            <div className="absolute inset-0 bg-slate-950/0 group-hover:bg-slate-950/40 transition-colors flex items-center justify-center">
                              <span className="text-white text-3xl opacity-0 group-hover:opacity-100 transition-opacity drop-shadow-md">
                                <i className="fa-solid fa-magnifying-glass-plus" aria-hidden="true" />
                              </span>
                            </div>
                            <span className="sr-only">Klik for at forstørre</span>
                          </button>
                        )
                      })}
                    </div>
                  </div>
                )
              })}
            </div>
          )}

          {/* CTA Banner at the bottom */}
          <div className="mt-16 bg-slate-900 rounded-2xl p-8 sm:p-12 text-white border border-slate-800 shadow-xl flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="space-y-2 text-center md:text-left max-w-xl">
              <h3 className="text-xl sm:text-2xl font-bold tracking-tight">
                Vil du have demonstreret Krüger Wet Blaster?
              </h3>
              <p className="text-slate-300 text-sm sm:text-base leading-relaxed">
                Kontakt os for en uforpligtende fremvisning eller tag dine egne metalemner med og se resultatet med det samme.
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-3">
              <Link
                to="/kontakt"
                className="inline-flex items-center gap-2 bg-sky-600 hover:bg-sky-500 text-white font-semibold px-6 py-3 rounded-md transition-colors text-sm shadow-sm"
              >
                <span>Kontakt os</span>
                <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true" />
              </Link>
              <Link
                to="/produkter"
                className="inline-flex items-center gap-2 bg-slate-800 hover:bg-slate-700 text-slate-200 hover:text-white font-medium px-5 py-3 rounded-md transition-colors text-sm border border-slate-700"
              >
                <span>Se maskiner</span>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Lightbox Modal */}
      {lightboxState.isOpen && (
        <LightboxModal
          images={lightboxState.images}
          activeIndex={lightboxState.index}
          onClose={closeLightbox}
          onIndexChange={(newIdx) => setLightboxState((prev) => ({ ...prev, index: newIdx }))}
        />
      )}
    </>
  )
}
